import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Loader2, ShipWheel } from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiClient } from '../api/client';
import argocdIcon from '../assets/icons/argocd.svg';

export default function ArgoCDIntegration() {
  const [detectedClusters, setDetectedClusters] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadStatus = async () => {
      try {
        const clusters = await apiClient.getArgoCDClusters();
        setDetectedClusters(clusters);
      } catch { /* ignore */ } finally { setLoading(false); }
    };
    loadStatus();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const autoDetected = detectedClusters.length > 0;

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <img src={argocdIcon} alt="ArgoCD" className="h-8 w-8 object-contain" />
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  ArgoCD
                  {autoDetected && (
                    <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-normal">Detected</Badge>
                  )}
                </h1>
                <p className="text-sm text-muted-foreground">GitOps deployment sync and health monitoring</p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {autoDetected ? (
              <>
                <p className="text-sm text-muted-foreground">
                  Argo CD was detected on the following clusters. Argo CD triggers and actions are available in the workflow builder.
                </p>
                <div className="flex flex-wrap gap-2">
                  {detectedClusters.map(c => (
                    <div key={c.id} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border bg-muted/30">
                      <ShipWheel className="w-4 h-4 text-foreground shrink-0" />
                      <span className="text-sm font-medium">{c.name}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  Argo CD is detected automatically by the Kestrel operator. Install the operator in a
                  cluster running Argo CD and the integration will activate automatically
                  — no credentials required.
                </p>
                <div className="p-3 rounded-lg border bg-muted/30">
                  <p className="text-sm font-medium mb-1">Requirements</p>
                  <ul className="text-xs text-muted-foreground list-disc pl-4 space-y-1">
                    <li>Kestrel operator installed in the cluster</li>
                    <li>Argo CD installed in the cluster (argocd-server service discoverable by the operator)</li>
                  </ul>
                </div>
                <Button asChild variant="outline" size="sm">
                  <Link to="/onboard-cluster">Onboard a cluster</Link>
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
